// src/App.js
import React, { useState, useEffect } from 'react';
import ContactForm from './components/ContactForm';
import './styles.css';

const App = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Track mouse position for parallax effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Calculate parallax effect values
  const calcParallax = (strength = 20) => {
    const x = (mousePosition.x / window.innerWidth - 0.5) * strength;
    const y = (mousePosition.y / window.innerHeight - 0.5) * strength;
    return { x, y };
  };

  const parallax = calcParallax();

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden font-sans">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-black to-purple-950 opacity-80"></div>
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-1 h-1 bg-indigo-400 rounded-full opacity-60 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDuration: `${3 + Math.random() * 7}s`,
              transform: `scale(${Math.random() * 2 + 0.5})`,
            }}
          />
        ))}
      </div>
      
      {/* Light beam effect */}
      <div 
        className="absolute left-1/2 top-0 w-96 h-screen opacity-20 bg-gradient-to-b from-purple-500 to-transparent"
        style={{
          transform: `translateX(-50%) rotate(${parallax.x * 2}deg)`,
          filter: 'blur(30px)',
        }}
      ></div>

      <div className="relative z-10">
        {/* Header with experimental navigation */}
        <header className="flex justify-between items-center py-8 px-8 md:px-16">
          <div className="flex items-center space-x-2">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-purple-400">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M18 13 L6 13" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7 L12 19" stroke="currentColor" strokeWidth="2" />
            </svg>
            <span className="text-xl font-light tracking-widest uppercase">SQUARE SPHERE</span>
          </div>
        </header>

        {/* Main content */}
        <main className="relative min-h-screen flex flex-col items-center justify-center px-6 md:px-16 pt-20 pb-32">
          {/* Simple centered logo with parallax effect */}
          <div 
            className="mb-10 relative"
            style={{
              transform: `translate(${parallax.x * 1.5}px, ${parallax.y * 1.5}px)`,
            }}
          >
            <svg viewBox="0 0 24 24" className="w-20 h-20 md:w-28 md:h-28 text-purple-400">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M18 13 L6 13" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7 L12 19" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          
          {/* Title with unique typography and animations */}
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-center mb-8 tracking-tight leading-none"
            style={{
              transform: `translate(${parallax.x * -0.5}px, ${parallax.y * -0.5}px)`,
            }}
          >
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">SQUARE</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">SPHERE</span>
            <span className="block text-xl md:text-2xl font-light tracking-widest mt-4 text-gray-400">STUDIOS</span>
          </h1>
          
          {/* Tagline with staggered reveal effect */}
          <p className="text-lg md:text-xl text-center max-w-xl mx-auto mb-16 text-gray-300 font-light tracking-wide">
            <span className="block opacity-80">Where impossible geometries</span>
            <span className="block opacity-90">meet innovative design solutions</span>
          </p>
          
          {/* Contact Form */}
          <ContactForm />
        </main>

        {/* Footer */}
        <footer className="relative z-10 py-8 text-center">
          <p className="text-gray-500 text-sm font-light">
            © {new Date().getFullYear()} Square Sphere Studios. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default App;
